import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import MuiAppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Fab from "@mui/material/Fab";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";

import Divider from "@mui/material/Divider";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import SystemSecurityUpdateGoodOutlinedIcon from "@mui/icons-material/SystemSecurityUpdateGoodOutlined";
import VolunteerActivismOutlinedIcon from "@mui/icons-material/VolunteerActivismOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

import { setNotifications, setPermission, setUser, setProcessQueue, sideNavToggle } from "../../store/actions";
import AuthService from "../../service/auth";
import vortex_logo_min from "../../assets/images/vortex_logo_min.png";
import NotificationsList from "./NotificationsList";
import ProcessQueue from "./ProcessQueue";
import { setPrevState } from "../../util/common";
import { ConfirmPopup } from "../Common";
import ProcessQueueService from "../../service/ProcessQueueService";
import AccountMenu from "../kmk/AppMenu";
import WorkspaceApply from "./WorkspaceApply";
import "./Appbar.css";

import UserJourney from "../UserJourney/UserJourney";
import UserJourney1 from "../UserJourney/UserJourney1";

const useStyles = makeStyles()((theme) => ({
  menuItem: {
    padding: "10px 16px",

    "& .MuiSvgIcon-root": {
      color: "#424242",
    },

    "& span": {
      marginLeft: "18px",
    },
  },
  appBar: {
    color: "black",
    backgroundColor: "white",
    boxShadow: "0px 3px 40px #4343430A",
    maxHeight: "45px !important",
    width: "100%",
    position: "relative",
  },
  toolBar: {
    display: "flex",
    pr: "24px", // keep right padding when drawer closed
    marginTop: "-10px",
    minHeight: "64px",
  },
  fab: {
    backgroundColor: "white",
    marginRight: "36px",
    width: "25px",
    height: "25px",
    minHeight: "0px",
    margin: "10px 36px 10px 0",
  },
  avatar: {
    width: "24px",
    height: "24px",
    fontSize: "0.7rem",
    padding: "0",
    backgroundColor: "#5243AA",
    lineHeight: 0,
    alignContent: "center",
    alignItems: "center",
  },
}));

const Appbar = ({ children }) => {
  const { Workspace_id } = useParams();
  const { classes } = useStyles();

  const [openConfirm, setOpenConfirm] = useState({
    open: false,
    loadingForConfirm: false,
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const permissions = useSelector((state) => state.permissions);
  const processQueue = useSelector((state) => state.processQueue);
  const sideNavbar = useSelector((state) => state.sideNavbar);

  const authService = new AuthService();

  const isMenuOpen = Boolean(anchorEl);

  const [showIntro, setShowIntro] = useState(!localStorage.getItem("introShown"));
  const [showIntro1, setShowIntro1] = useState(!localStorage.getItem("introShown1"));

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const updateAndLogout = async () => {
    try {
      setPrevState(setOpenConfirm, { loadingForConfirm: true });
      // Create an array of promises for the updateProcessQueue calls
      const updatePromises = processQueue.currentQueue.map(async (process) => {
        const queueService = new ProcessQueueService();
        return await queueService.updateProcessQueue(process.ProcessQueue_id, {
          Status: 3,
          Message: `${process.file.name ?? "File"} failed to upload. Upload process interrupted by user.`,
        });
      });
      // Wait for all updateProcessQueue calls to complete
      await Promise.all(updatePromises);
      // Once all updates are done, proceed to logout
      await logout();
      setPrevState(setOpenConfirm, { open: false, loadingForConfirm: false });
    } catch (error) {
      await logout();
    }
  };

  const logout = async () => {
    await authService.logout();
    handleMenuClose();
    dispatch(setUser(null));
    dispatch(setPermission(false));
    dispatch(
      setNotifications({
        todayNotifications: [],
        olderNotifications: [],
        notSeenNotificationCount: 0,
      })
    );
    dispatch(setProcessQueue({ todayQueue: [], olderQueue: [], currentQueue: [], inProgress: false, refresh: false }));
    navigate("/");
  };

  const getNameInitials = (firstName, lastName) => {
    return firstName?.charAt(0) + lastName?.charAt(0);
  };

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          style: {
            borderRadius: "16px",
            marginTop: "10px",
          },
        },
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
      sx={{
        mt: "20px",
      }}
    >
      <MenuItem className={classes.menuItem} onClick={() => navigate("/my-profile")} data-id="myProfile">
        <BadgeOutlinedIcon />
        <span>My Profile</span>
      </MenuItem>
      {user && user.currentUser?.connections > 0 && permissions && permissions.includes("ACCESS_CREDENTIALS") ? (
        <MenuItem
          className={classes.menuItem}
          onClick={() => navigate("/my-access-credentials")}
          data-id="myAccessCredentials"
        >
          <VpnKeyOutlinedIcon />
          <span>My Access Credentials</span>
        </MenuItem>
      ) : (
        ""
      )}
      <MenuItem className={classes.menuItem} onClick={() => navigate("/feedback-suggestions")} data-id="featureRequest">
        <VolunteerActivismOutlinedIcon />
        <span>Feedback and Suggestions</span>
      </MenuItem>
      <MenuItem className={classes.menuItem} onClick={() => navigate("/system-info")} data-id="systemInfo">
        <SystemSecurityUpdateGoodOutlinedIcon />
        <span>System Information</span>
      </MenuItem>
      <MenuItem
        className={classes.menuItem}
        onClick={() => {
          if (processQueue.currentQueue.length !== 0) setPrevState(setOpenConfirm, { open: true });
          else logout();
        }}
        data-id="signOut"
      >
        <LogoutOutlinedIcon /> <span>Sign Out</span>
      </MenuItem>
      {openConfirm.open && (
        <ConfirmPopup
          configData={{
            popupTitle: (
              <Box sx={{ m: 0, p: 0, display: "flex", alignItems: "center" }}>
                <ReportProblemIcon color="warning" sx={{ mr: "8px" }} />
                <Typography>Upload in Progress</Typography>
              </Box>
            ),
            popupMessage: `You have an upload process in progress. Are you sure you want to log out?`,
            handleCancel: () => setPrevState(setOpenConfirm, { open: false }),
            handleClick: () => updateAndLogout(),
            loadingForConfirm: openConfirm.loadingForConfirm,
          }}
        />
      )}
    </Menu>
  );

  return (
    <Box>
      <AppBar elevation={0} className={classes.appBar} position="absolute" open={sideNavbar.open}>
        <Toolbar className={classes.toolBar}>
          <Box
            sx={(theme) => ({
              marginRight: "24px",
              [theme.breakpoints.up("769")]: {
                display: "none",
              },
            })}
          >
            <img
              src={vortex_logo_min}
              style={{
                width: "30px",
              }}
              alt="logo"
            ></img>
          </Box>
          <Fab edge="start" onClick={() => dispatch(sideNavToggle())} size="small" className={classes.fab}>
            {sideNavbar.open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </Fab>

          <Grid container sx={{ width: "100%" }}>
            <Grid size={6} alignSelf="center">
              <Typography component="h1" variant="h6" color="inherit" noWrap sx={{ flexGrow: 1 }}>
                {children}
              </Typography>
            </Grid>
            {/* Adding this changes for controling long names with wrapping */}
            <Grid
              size={6}
              textAlign="right"
              alignSelf="center"
              display={"flex"}
              alignItems={"center"}
              justifyContent={"flex-end"}
            >
              {Workspace_id && (
                <>
                  {showIntro1 && <UserJourney1 setShowIntro1={setShowIntro1} />}
                  <AccountMenu showIntro={showIntro} />
                  <WorkspaceApply showIntro={showIntro} />
                  <Divider
                    orientation="vertical"
                    variant="middle"
                    flexItem
                    style={{ marginLeft: "8px", marginRight: "16px" }}
                  />
                </>
              )}
              {showIntro && <UserJourney setShowIntro={setShowIntro} />}
              <ProcessQueue showIntro={showIntro} />

              <NotificationsList />

              <IconButton
                data-id="accountCircleIcon"
                color="inherit"
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
              >
                <Tooltip title="Your profile and settings" arrow>
                  <Avatar className={classes.avatar}>
                    <div
                      style={{
                        padding: 0,
                        margin: 0,
                        textAlign: "center",
                      }}
                    >
                      {getNameInitials(user.currentUser?.FirstName, user.currentUser?.LastName)}
                    </div>
                  </Avatar>
                </Tooltip>
              </IconButton>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      {renderMenu}
    </Box>
  );
};

export default React.memo(Appbar);

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    // marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));
